import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import useStore from "../../../helpers/useStore";
import FlatCard from "../../../atoms/FlatCard";
import {
  AddressDto,
  AddressDtoTypeEnum,
  PatientDto,
  SalesTemplateDto,
  TherapyDetailsDto,
} from "../../../api-client";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { useCurrentLocation } from "../../../stores/User/user.queries";
import { visitTypeToIcon } from "../../../helpers/visitTypeMapping";
import { SalesStoreContext } from "../../../stores/Sales/sales.provider";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useMasterTables, getTherapyId } from "../../../stores/MasterTables";
import GiftCertificatesTable from "./GiftCertificatesTable";
import SalesTable from "./SalesTable";
import SelectableTable from "../SelectableTable";
import { usePatient } from "../../../stores/Patients";

interface TherapyDto {
  id: string;
  lineItem: string;
  abbreviation: string;
  description: string;
  duration: number;
  maxParticipants: number;
  tarifGroup: { id: string };
}

const Sales = observer(() => {
  const SalesStore = useStore(SalesStoreContext);
  const PatientStore = useStore(PatientsContext);
  const { selectedPatientId } = PatientStore;

  const { data: patient } = usePatient(selectedPatientId!);

  const { getSaleServices } = SalesStore;
  const AppointmentStore = useStore(AppointmentContext);
  const { createAppointmentsForServices } = AppointmentStore;
  const [dataRefreshFlag, setDataRefreshFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saleServices, setSaleServices] = useState<TherapyDetailsDto[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [salesTemplate, setSalesTemplate] = useState<SalesTemplateDto>({
    therapies: [],
    amount: undefined,
    addressId: "",
  });
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const currentLocation = useCurrentLocation();
  const [selectedAddressType, setSelectedAddressType] = useState<AddressDtoTypeEnum | undefined>();
  const [visitFee, setVisitFee] = useState<string | undefined>();
  const { data: masterTablesData } = useMasterTables();
  const [loadingSave, setLoadingSave] = useState(false);

  const visitFeeOptions = [
    // { label: "HB2K", value: "HB2K" },
    // { label: "HB7", value: "HB7" },
    { label: "HB", value: "HB" },
    // { label: "HB2", value: "HB2" },
  ];

  // Patient's own addresses plus the current location's Praxis address, so that a
  // Praxisbesuch can be sold without the patient having a practice address on file.
  const addresses: AddressDto[] = useMemo(() => {
    const patientAddresses = (patient?.addresses || []).filter(
      (address) => address.contactInfo?.addressLine1 && address.contactInfo.zip
    );

    const praxisAddress = currentLocation?.address;
    if (praxisAddress?.contactInfo?.addressLine1 && praxisAddress.contactInfo.zip) {
      // Remove any existing address of type "praxis" before adding the location's one
      return [
        ...patientAddresses.filter((addr) => addr.type !== AddressDtoTypeEnum.Praxis),
        praxisAddress,
      ];
    }

    return patientAddresses;
  }, [patient, currentLocation]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSaleServices();
        const filteredData = data.filter((service: any) => service.duration !== null);
        setSaleServices(filteredData);
      } catch (error) {
        notificationStore.showMessage("Fehler beim Laden der Leistungen", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectService = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleAmountChange = (e: any) => {
    setSalesTemplate({
      ...salesTemplate,
      amount: Math.max(1, parseInt(e.target.value)),
    });
  };

  const handleAddressChange = (e: any) => {
    const selectedId = e.target.value;
    const selectedAddr = addresses.find((addr) => addr.id === selectedId);

    setSelectedAddressType(selectedAddr?.type);
    setSalesTemplate({
      ...salesTemplate,
      addressId: selectedId,
    });

    // Clear visitFee if Praxis
    if (selectedAddr?.type === AddressDtoTypeEnum.Praxis) {
      setVisitFee(undefined);
    }
    if (
      selectedAddr?.type === AddressDtoTypeEnum.Haus ||
      selectedAddr?.type === AddressDtoTypeEnum.Heim
    ) {
      setVisitFee("HB");
    }
  };

  const handleConfirm = async () => {
    if (!salesTemplate.amount) {
      notificationStore.showMessage("Bitte Betrag eingeben", "error");
      return;
    }
    try {
      setLoadingSave(true);
      const therapies = saleServices
        .filter((service) => selectedServiceIds.includes(service.id!))
        .map((service) => ({
          ...service,
          id: service.id!,
        }));
      let visitFeeValue;
      if (visitFee) {
        let trafiGroupId = (therapies[0] as TherapyDto)?.tarifGroup?.id as string;
        visitFeeValue = getTherapyId(masterTablesData || [], trafiGroupId, visitFee);
      }

      const updatedSalesTemplate: SalesTemplateDto = {
        ...salesTemplate,
        patient: patient,
        therapies: therapies,
        visitFee: visitFeeValue ? { id: visitFeeValue } : undefined,
      };

      const response = await createAppointmentsForServices(updatedSalesTemplate);
      setSalesTemplate({ therapies: [], amount: undefined, addressId: "" });
      setSelectedServiceIds([]);
      setDataRefreshFlag(!dataRefreshFlag);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(`Fehler beim Anlegen der Termine: ${errorMessage}`, "error");
    } finally {
      setLoadingSave(false);
    }
  };
  const columns = [
    {
      label: "Heilmittel",
      render: (s: any) => s.abbreviation,
    },
    {
      label: "Beschreibung",
      render: (s: any) => s.description || "-",
    },
    {
      label: "Preis (€)",
      render: (s: any) => `${s.price} €`,
    },
  ];

  return (
    <Grid container spacing={3}>
      {/* First Card: Gift Certificate Creation */}
      <Grid item xs={12} md={5}>
        <Card sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
          <CardHeader title="Verkauf" />
          <CardContent sx={{ padding: 2, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Table Section */}

            <Typography textAlign="left">
              Um den Gutscheinwert zu berechnen, wähle zuerst die gewünschten Leistungen aus.
            </Typography>

            <FlatCard title={undefined}>
              <SelectableTable
                columns={columns}
                data={saleServices}
                isLoading={loading}
                onRowClick={(service) => handleSelectService(service.id!)}
                isSelected={(service) => selectedServiceIds.includes(service.id!)}
                emptyMessage="Keine Leistungen gefunden."
                rowHeight={60}
              />
            </FlatCard>

            <Typography textAlign="left">
              Zunächst wähle Haus- oder Praxisbesuch und bestimmte die Anzahl der Termine.
            </Typography>
            {/* Bottom-Right Summary Box */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box
                sx={{
                  width: 300,
                  p: 3,
                  backgroundColor: "grey.100",
                  borderRadius: 2,
                }}
              >
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Adresse auswählen</InputLabel>
                  <Select
                    value={salesTemplate.addressId}
                    onChange={handleAddressChange}
                    label="Adresse auswählen"
                  >
                    {addresses?.map((addressObj) => (
                      <MenuItem value={addressObj.id} key={addressObj.id}>
                        {addressObj.contactInfo?.addressLine1}{" "}
                        {addressObj.type
                          ? visitTypeToIcon[addressObj.type.toLowerCase()]
                          : undefined}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedAddressType === AddressDtoTypeEnum.Haus && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body1">Pauschale-{visitFee}</Typography>
                    </Box>
                  )}
                </FormControl>

                <TextField
                  type="number"
                  label="Menge"
                  variant="outlined"
                  fullWidth
                  value={salesTemplate.amount ?? ""}
                  onChange={handleAmountChange}
                  sx={{ mb: 2 }}
                  required
                  InputLabelProps={{ shrink: true }}
                />

                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={!salesTemplate.amount}
                >
                  {loadingSave ? (
                    <CircularProgress size={24} sx={{ color: "black" }} />
                  ) : (
                    "Termine erstellen"
                  )}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Separate Card for Unpaid Gift Certificates */}
      <Grid item xs={7}>
        <Card>
          <CardHeader title="Selbstzahler Leistungen" />
          <CardContent>
            <SalesTable
              patientId={patient?.id as number}
              dataRefreshFlag={dataRefreshFlag}
              setDataRefreshFlag={setDataRefreshFlag}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={5}>
        <Card>
          <CardHeader title="Geschenkgutscheine" />
          <CardContent>
            <GiftCertificatesTable
              patientId={selectedPatientId}
              dataRefreshFlag={dataRefreshFlag}
              setDataRefreshFlag={setDataRefreshFlag}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
});

export default Sales;
